import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Activity, 
  Target,
  ArrowRight,
  Clock,
  CheckCircle,
  Utensils,
  Moon,
  Wine,
  Footprints,
  RefreshCw,
  Calendar
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';

const PlanPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!user?.is_paid) {
      navigate('/pricing');
      return;
    }
    fetchPlan();
  }, [user]);

  const fetchPlan = async () => {
    try {
      const response = await api.get('/plan/current');
      setPlan(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        // Generate plan if not exists
        await generatePlan();
      } else {
        toast.error('Failed to load plan');
      }
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/plan/generate');
      setPlan(response.data);
      await refreshUser();
      toast.success('Your 90-day plan is ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate plan');
      if (error.response?.status === 402) {
        navigate('/pricing');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/plan/regenerate');
      setPlan(response.data);
      toast.success('Plan updated based on your progress');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot regenerate yet');
    } finally {
      setGenerating(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'nutrition': return <Utensils className="w-5 h-5" />;
      case 'sleep': return <Moon className="w-5 h-5" />;
      case 'alcohol': return <Wine className="w-5 h-5" />;
      case 'movement': return <Footprints className="w-5 h-5" />;
      default: return <Target className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'nutrition': return '#F59E0B';
      case 'sleep': return '#8B5CF6';
      case 'alcohol': return '#EF4444';
      case 'movement': return '#10B981';
      default: return '#2F3E30';
    }
  };

  if (loading || generating) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <div className="text-center">
          <Target className="w-12 h-12 text-[#2F3E30] animate-pulse mx-auto mb-4" />
          <p className="text-[#525252]">
            {generating ? 'Generating your personalized plan...' : 'Loading your plan...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-[#525252] hover:text-[#1A1C19]"
            >
              Dashboard
            </Button>
            <Button 
              onClick={() => navigate('/checkin')}
              className="bg-[#2F3E30] hover:bg-[#3A4D3B]"
              data-testid="weekly-checkin-btn"
            >
              Weekly Check-in
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Plan Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/20 border border-[#10B981]/30 text-[#10B981] mb-4">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Plan Active</span>
            </div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              YOUR 90-DAY CORRECTION PLAN
            </h1>
            <p className="text-[#525252]">
              4 targeted rules to shift your metabolic trajectory
            </p>
          </div>

          {/* Plan Info */}
          {plan && (
            <div className="grid grid-cols-3 gap-4 mb-12">
              <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4 text-center">
                <Clock className="w-6 h-6 text-[#2F3E30] mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>{plan.duration_days}</p>
                <p className="text-xs text-[#525252]">Days Total</p>
              </div>
              <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4 text-center">
                <Calendar className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>Week {plan.week_number}</p>
                <p className="text-xs text-[#525252]">Current Week</p>
              </div>
              <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4 text-center">
                <Target className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>{plan.rules?.length || 4}</p>
                <p className="text-xs text-[#525252]">Active Rules</p>
              </div>
            </div>
          )}

          {/* Rules */}
          <div className="space-y-6 mb-12">
            {plan?.rules?.map((rule, index) => (
              <div 
                key={rule.id}
                className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 lg:p-8"
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${getCategoryColor(rule.category)}20`, color: getCategoryColor(rule.category) }}
                  >
                    {getCategoryIcon(rule.category)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-xs uppercase tracking-wider text-[#525252]">Rule {index + 1}</span>
                        <h3 className="text-xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                          {rule.name}
                        </h3>
                      </div>
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getCategoryColor(rule.category)}20`,
                          color: getCategoryColor(rule.category)
                        }}
                      >
                        {rule.category}
                      </span>
                    </div>
                    
                    {/* The Action */}
                    <p className="text-lg text-[#1A1C19] mb-4">{rule.description}</p>
                    
                    {/* Metric & Target */}
                    {(rule.metric || rule.success_target) && (
                      <div className="flex flex-col sm:flex-row gap-3 mb-4">
                        {rule.metric && (
                          <div className="flex-1 p-3 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD]">
                            <p className="text-xs uppercase tracking-wider text-[#525252] mb-1">Track</p>
                            <p className="text-sm text-[#1A1C19] font-medium">{rule.metric}</p>
                          </div>
                        )}
                        {rule.success_target && (
                          <div className="flex-1 p-3 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20">
                            <p className="text-xs uppercase tracking-wider text-[#10B981] mb-1">Target</p>
                            <p className="text-sm text-[#10B981] font-medium">{rule.success_target}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Why Explanation */}
                    <div className="p-4 rounded-lg bg-[#E8E4DC] border border-[#D1D1CD]">
                      <p className="text-xs uppercase tracking-wider text-[#525252] mb-2">Why This Works</p>
                      <p className="text-sm text-[#525252] leading-relaxed">{rule.why_explanation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/checkin')}
              className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white px-8"
              data-testid="start-checkin-btn"
            >
              Start Weekly Check-in
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={handleRegenerate}
              variant="outline"
              className="border-[#D1D1CD] hover:bg-[#EBEBE6]"
              disabled={generating}
              data-testid="adjust-plan-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Adjust My Plan
            </Button>
          </div>

          <p className="text-center text-[#525252] text-sm mt-4">
            Plan can be adjusted every 2 weeks based on your progress
          </p>
        </div>
      </main>
    </div>
  );
};

export default PlanPage;
